import debounce from 'lodash/debounce'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'hooks/use-redux'
import type { Playlist } from 'types/playlist'
import type { State } from 'types/redux'
import emptySearch1x from 'assets/images/empty-search.png'
import emptySearch2x from 'assets/images/empty-search@2x.png'
import { clientLibraryPlaylistsUpdate } from '@disco/state-management/actions/client-library-playlist-actions'
import {
  getKeyByPlaylistType,
  loadNextPlaylists,
  searchPlaylists,
} from '@disco/state-management/actions/playlist-actions'
import PlaylistGridItem from 'components/common/playlist-grid-item'
import Row from 'components/common/row'
import DocumentHeadTags from 'components/document-head-tags'
import Button from 'components/ui/button'
import Checkbox from 'components/ui/checkbox'
import EmptyState from 'components/ui/empty-state'
import Grid from 'components/ui/grid'
import Heading from 'components/ui/heading'
import InfiniteScroll from 'components/ui/infinite-scroll'
import Input from 'components/ui/input'
import ModalWindow from 'components/ui/modal-window'

const LibrariesLibraryPlaylistsAdd = (): React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const params = useParams<{ libraryId: string }>()
  const libraryId: number = parseInt(params.libraryId!, 10)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [query, setQuery] = useState<string>('')
  const [isLoadingNext, setIsLoadingNext] = useState<boolean>(false)
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const search: Record<string, any> = useSelector((state: State) => {
    const key = getKeyByPlaylistType('playlist')
    return state.clientLibraries.search[key]
  })
  useEffect(() => {
    dispatch(searchPlaylists(query, 'playlist'))
  }, [])
  const debouncedQuery = useRef(
    debounce((query: string) => {
      setIsSearching(false)
      dispatch(searchPlaylists(query, 'playlist'))
    }, 300),
  ).current
  const updateSearchQuery = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsLoadingNext(false)
      setIsSearching(true)
      setQuery(event.target.value)
      debouncedQuery(event.target.value)
    },
    [debouncedQuery],
  )
  const updateSelectedIds = useCallback((id: number) => {
    setSelectedIds((prevState: number[]) => {
      if (prevState.includes(id)) {
        return prevState.filter((item: number) => item !== id)
      } else {
        return [...prevState, id]
      }
    })
  }, [])
  const loadNext = useCallback(
    (next: string) => {
      setIsLoadingNext(true)
      dispatch(loadNextPlaylists(next, 'playlist'))
    },
    [dispatch],
  )
  const handleCancel = useCallback(
    (event: React.SyntheticEvent<any>) => {
      event.preventDefault()
      navigate(-1)
    },
    [dispatch],
  )
  const handleSubmit = useCallback(
    (event: React.SyntheticEvent<any>) => {
      event.preventDefault()
      dispatch(clientLibraryPlaylistsUpdate(libraryId, selectedIds, []))
      navigate(-1)
    },
    [dispatch, libraryId, selectedIds],
  )

  const renderContent = () =>
    search.loaded &&
    search.items.length === 0 &&
    !isSearching &&
    query !== '' ? (
      <EmptyState>
        <img
          srcSet={`${emptySearch1x}, ${emptySearch2x} 2x`}
          width="177"
          height="160"
        />
        <p>
          Sorry! We couldn’t find anything for that search. Please try another
          query.
        </p>
      </EmptyState>
    ) : (
      <InfiniteScroll next={search.next} loadNext={loadNext}>
        <Grid size="large">
          {search.items.map((playlist: Playlist) => {
            const isPublished: boolean = playlist['client_library_ids']
              ? playlist['client_library_ids'].includes(libraryId)
              : false
            const isSelected: boolean = selectedIds.includes(
              playlist['playlist_id'],
            )
            return (
              <PlaylistGridItem
                key={playlist['playlist_id']}
                playlist={playlist}
                status={isPublished ? 'Published' : undefined}
                overlayActions={
                  !isPublished && <Checkbox checked={isSelected} />
                }
                overlayVisible={isSelected}
                onClick={!isPublished ? updateSelectedIds : undefined}
              />
            )
          })}
        </Grid>
      </InfiniteScroll>
    )

  return (
    <ModalWindow
      header={
        <>
          <Heading size={700}>Publish new playlist</Heading>
          <div className="form-note">
            To add playlists to your Catalog, browse them below or search for
            them, and click the checkbox.
          </div>
        </>
      }
      actions={
        <Button
          appearance="primary"
          caption={
            selectedIds.length > 0
              ? `Publish ${selectedIds.length} ${
                  selectedIds.length === 1 ? 'playlist' : 'playlists'
                }`
              : 'Publish playlists'
          }
          disabled={search.loading || selectedIds.length === 0}
          onClick={handleSubmit}
        />
      }
      isOpen={true}
      onClose={handleCancel}
    >
      <DocumentHeadTags title="Publish new playlist | DISCO" />
      <Row columnsSize={[3]}>
        <div>
          <Input
            icon="search"
            placeholder="Search"
            value={query}
            onChange={updateSearchQuery}
          />
        </div>
      </Row>
      {!isLoadingNext && search.loading ? (
        <span>Loading...</span>
      ) : (
        renderContent()
      )}
    </ModalWindow>
  )
}

export default LibrariesLibraryPlaylistsAdd
